import React from "react";
import { Document, Page, View, StyleSheet } from "@react-pdf/renderer";
import { StrikeZonePDF } from "../components/charting/pdf/StrikeZonePDF";
import { roundTo } from "../utils/mathUtils";
import { ReportHeader } from "../components/charting/pdf/shared/ReportHeader";
import { ReportFooter } from "../components/charting/pdf/shared/ReportFooter";
import { ReportTable } from "../components/charting/pdf/shared/ReportTable";
import { reportStyles } from "../components/charting/pdf/shared/styles";

const styles = StyleSheet.create({
  visualizationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 20,
    marginBottom: 20,
  },
  zoneBox: {
    flex: 1,
    padding: 10,
    border: 1,
    borderColor: "#000000",
  },
});

const BALL_WIDTH = 2.9;

const FRAMING_ZONE_BOUNDS = {
    11: {
      xMin: -8.5 - BALL_WIDTH * 1.25,
      xMax: -8.5 + BALL_WIDTH * 1.25,
      yMin: 42 - BALL_WIDTH * 1.25,
      yMax: 42 + BALL_WIDTH * 1.25,
  },
    12: {
      xMin: -8.5 + BALL_WIDTH * 1.25,
      xMax: 8.5 - BALL_WIDTH * 1.25,
      yMin: 42 - BALL_WIDTH * 1.25,
      yMax: 42 + BALL_WIDTH * 1.25,
  },
    13: {
      xMin: 8.5 - BALL_WIDTH * 1.25,
      xMax: 8.5 + BALL_WIDTH * 1.25,
      yMin: 42 - BALL_WIDTH * 1.25,
      yMax: 42 + BALL_WIDTH * 1.25,
    },
    14: {
      xMin: -8.5 - BALL_WIDTH * 1.25,
      xMax: -8.5 + BALL_WIDTH * 1.25,
      yMin: 18 + BALL_WIDTH * 1.25,
      yMax: 42 - BALL_WIDTH * 1.25,
  },
    16: {
      xMin: 8.5 - BALL_WIDTH * 1.25,
      xMax: 8.5 + BALL_WIDTH * 1.25,
      yMin: 18 + BALL_WIDTH * 1.25,
      yMax: 42 - BALL_WIDTH * 1.25,
  },
    17: {
      xMin: -8.5 - BALL_WIDTH * 1.25,
      xMax: -8.5 + BALL_WIDTH * 1.25,
      yMin: 18 - BALL_WIDTH * 1.25,
      yMax: 18 + BALL_WIDTH * 1.25,
  },
    18: {
      xMin: -8.5 + BALL_WIDTH * 1.25,
      xMax: 8.5 - BALL_WIDTH * 1.25,
      yMin: 18 - BALL_WIDTH * 1.25,
      yMax: 18 + BALL_WIDTH * 1.25,
  },
    19: {
      xMin: 8.5 - BALL_WIDTH * 1.25,
      xMax: 8.5 + BALL_WIDTH * 1.25,
      yMin: 18 - BALL_WIDTH * 1.25,
      yMax: 18 + BALL_WIDTH * 1.25,
  },
};

const FRAMING_ZONES = [11, 12, 13, 14, 16, 17, 18, 19];

const isPitchInFramingZone = (pitch) => {
  return FRAMING_ZONES.some((zone) => {
    const bounds = FRAMING_ZONE_BOUNDS[zone];
    if (!bounds) return false;
    const x = pitch.location?.x;
    const y = pitch.location?.y;
    if (x === undefined || y === undefined) return false;
    return (
      x >= bounds.xMin &&
      x <= bounds.xMax &&
      y >= bounds.yMin &&
      y <= bounds.yMax
    );
  });
};


const calculateFramingMetrics = (pitches) => {
  // First filter for only called strikes and balls
  const relevantPitches = pitches.filter(
    (pitch) => pitch.result === "called_strike" || pitch.result === "ball"
  );

  // Then filter for framing zones
  const framingPitches = relevantPitches.filter((pitch) =>
    isPitchInFramingZone(pitch)
  );

  const metrics = {
    totalPitches: framingPitches.length,
    zoneStats: {},
  };

  FRAMING_ZONES.forEach((zone) => {
    const bounds = FRAMING_ZONE_BOUNDS[zone];
    const zonePitches = framingPitches.filter((pitch) => {
      const x = pitch.location?.x;
      const y = pitch.location?.y;
      if (x === undefined || y === undefined || !bounds) return false;
      return (
        x >= bounds.xMin &&
        x <= bounds.xMax &&
        y >= bounds.yMin &&
        y <= bounds.yMax
    );
    });

    const calledStrikes = zonePitches.filter(
      (pitch) => pitch.result === "called_strike"
    );
    const calledBalls = zonePitches.filter((pitch) => pitch.result === "ball");

    metrics.zoneStats[zone] = {
      total: zonePitches.length,
      calledStrikes: calledStrikes.length,
      calledBalls: calledBalls.length,
      framingRuns:
        zonePitches.length > 0
          ? (calledStrikes.length * 0.125 - calledBalls.length * 0.125).toFixed(
              1
            )
          : "0.0",
      strikeRate:
        zonePitches.length > 0
          ? ((calledStrikes.length / zonePitches.length) * 100).toFixed(1)
          : "0.0",
    };
  });

  return metrics;
};


export const CatcherReport = ({ charts = [] }) => {
  const catcherData = charts.reduce((acc, chart) => {
    const catcherName = chart.catcher?.name || "Unknown Catcher";
    if (!acc[catcherName]) {
      acc[catcherName] = {
        charts: [],
        latestDate: chart.date,
      };
    }
    acc[catcherName].charts.push(chart);
    if (new Date(chart.date) > new Date(acc[catcherName].latestDate)) {
      acc[catcherName].latestDate = chart.date;
    }
    return acc;
  }, {});

  return (
    <Document>
      {Object.entries(catcherData).map(([catcher, { charts, latestDate }]) => {
        const allPitches = charts.flatMap((chart) =>
          (chart.pitches || []).map((pitch) => ({
            ...pitch,
            zoneType: chart.zoneType,
          }))
        );

        const metrics = calculateFramingMetrics(allPitches);
        const framingPitches = allPitches.filter((pitch) =>
          isPitchInFramingZone(pitch)
        );

        const tableRows = FRAMING_ZONES.map((zone) => {
          const stats = metrics.zoneStats[zone];
          return {
            cells: [
              { value: `Zone ${zone}` },
              { value: stats.total },
              { value: stats.calledStrikes },
              { value: `${stats.strikeRate}%` },
              { value: roundTo(stats.framingRuns, 1) },
            ],
          };
        });

        return (
          <Page
            key={catcher}
            size="A4"
            orientation="portrait"
            style={reportStyles.page}
          >
            <ReportHeader
              title={`${catcher} - Framing Report`}
              date={new Date(latestDate).toLocaleDateString()}
            />

            <ReportTable
              headers={[
                "Zone",
                "Total Pitches",
                "Called Strikes",
                "Strike Rate",
                "Framing Runs",
              ]}
              rows={tableRows}
              cellWidths={["20%", "20%", "20%", "20%", "20%"]}
              cellStyles={{ header: Array(5).fill({ fontSize: 9 }) }}
            />

            {/* Strike Zone Visualization */}
            <View style={styles.visualizationContainer}>
              <View style={styles.zoneBox}>
                <View
                  style={{ justifyContent: "center", alignItems: "center" }}
                >
                  <StrikeZonePDF
                    pitches={framingPitches}
                    width={250}
                    height={250}
                    mode="zones"
                    metrics={metrics}
                  />
                </View>
              </View>

              <View style={styles.zoneBox}>
                <View
                  style={{ justifyContent: "center", alignItems: "center" }}
                >
                  <StrikeZonePDF
                    pitches={framingPitches}
                    width={250}
                    height={250}
                    colorBy="result"
                    showOnlyResults={["called_strike", "ball"]}
                  />
                </View>
              </View>
            </View>

            <ReportFooter />
          </Page>
        );
      })}
    </Document>
  );
};